import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, } from 'libphonenumber-js/max';
import { err, ok } from '../result.js';
import { compareForSort } from './collator.js';
import { countryName } from './countries.js';
/**
 * Telephone numbers, normalized to E.164 and kept alongside the form a person recognises.
 *
 * **Why E.164 is the stored form.** It is unambiguous, it is what a blind index has to be computed
 * over if `+34 612 34 56 78`, `0034612345678` and `612345678` are to find the same record, and it
 * is the only form that survives a person moving country. The national significant number is kept
 * as a secondary form because it is what an operator reads off a document and types into a search
 * box, and matching only the E.164 form would make a locally-written number unfindable.
 *
 * **Why the `max` metadata bundle.** The default `min` bundle carries enough metadata to guess a
 * country but not enough to reject an impossible subscriber number: it accepts `+34 999 999 999`,
 * which is not a Spanish number. `max` rejects it. This is a system whose records are checked at a
 * gate, so a number that merely looks plausible is not good enough, and the extra metadata is a
 * fixed cost paid once per bundle.
 *
 * **`ES` is the default country and it is a default, not an assumption.** A number written without
 * a country code is read as Spanish, which is right for a Spanish facility; a number written with
 * one is read as what it says, so a Moroccan or Ukrainian number entered in full is stored as such.
 */
/** The facility is in Spain, so a bare national number is Spanish unless it says otherwise. */
export const DEFAULT_PHONE_COUNTRY = 'ES';
/**
 * The regions the metadata can actually parse for.
 *
 * Not the same set as `COUNTRY_CODES`: this product accepts every ISO country as a value of a
 * `country` field, while the numbering-plan metadata knows only the regions that have one. The two
 * are kept apart deliberately, so that a field definition naming a default country the parser does
 * not know is refused when the field is DEFINED rather than silently ignored at every value.
 */
const SUPPORTED_REGIONS = new Set(getCountries());
/** Whether the metadata can parse a bare national number for this region. */
export function isSupportedPhoneCountry(value) {
    return SUPPORTED_REGIONS.has(value);
}
/**
 * Narrows a country code the parser understands, and refuses one it does not.
 *
 * Returning `undefined` rather than falling back to Spain is the safe half: with no default
 * country a bare national number is refused, where a silent fallback would attribute it to the
 * wrong country and store a number that reaches somebody else.
 */
function asRegion(value) {
    return isSupportedPhoneCountry(value) ? value : undefined;
}
/**
 * Parses and normalizes a telephone number.
 *
 * Returns a typed refusal rather than throwing, because an unparseable number is an expected
 * outcome of a person typing into a form, not a fault. The refusal carries only the code: the
 * value that failed is personal data and never appears in the failure, in a log or in a message.
 */
export function normalizePhone(input, defaultCountry = DEFAULT_PHONE_COUNTRY) {
    const trimmed = input.trim();
    if (trimmed.length === 0) {
        return err('VALIDATION_INVALID_PHONE');
    }
    const region = asRegion(defaultCountry);
    const parsed = parsePhoneNumberFromString(trimmed, region === undefined ? {} : { defaultCountry: region });
    if (!parsed?.isValid()) {
        return err('VALIDATION_INVALID_PHONE');
    }
    return ok({
        e164: parsed.number,
        national: parsed.nationalNumber,
        ...(parsed.country === undefined ? {} : { country: parsed.country }),
    });
}
/** Whether a value is a telephone number this product will store. */
export function isValidPhone(input, defaultCountry = DEFAULT_PHONE_COUNTRY) {
    return normalizePhone(input, defaultCountry).ok;
}
/**
 * The E.164 form, or `undefined`.
 *
 * A convenience for the many call sites that want the stored form and have already established the
 * value is valid; it exists so those sites do not each re-implement the unwrapping and quietly
 * disagree about what an invalid number should become.
 */
export function toE164(input, defaultCountry = DEFAULT_PHONE_COUNTRY) {
    const result = normalizePhone(input, defaultCountry);
    return result.ok ? result.value.e164 : undefined;
}
/** Built once per locale: 245 `Intl.DisplayNames` lookups are not free, and the result is frozen. */
const DIAL_CODE_OPTIONS = new Map();
/**
 * Every region with a numbering plan, as a dialling-code option, sorted by name.
 *
 * **The region set is the numbering plan's, deliberately, and not {@link COUNTRY_CODES}.** This
 * module already keeps the two apart: the product accepts every ISO country as the value of a
 * `country` field, while the metadata knows only the regions that have a plan. The seven ISO codes
 * with no plan are uninhabited or research territories, and the two regions the metadata knows that
 * ISO has not assigned — Ascension and Tristan da Cunha — are worth offering here for the same
 * reason Kosovo is in the country list: somebody holds a telephone number there. Nothing selected
 * in this picker is stored, so widening it costs nothing and narrowing it would leave a hole.
 */
export function dialCodeOptions(locale) {
    const held = DIAL_CODE_OPTIONS.get(locale);
    if (held !== undefined) {
        return held;
    }
    const built = Object.freeze(getCountries()
        .map((region) => ({
        value: region,
        // Wrapped rather than passed by reference: `Array.prototype.map` hands the index as a
        // second argument, which this function reads as a metadata bundle and throws on.
        callingCode: getCountryCallingCode(region),
        label: countryName(region, locale),
    }))
        .sort((left, right) => compareForSort(left.label, right.label)));
    DIAL_CODE_OPTIONS.set(locale, built);
    return built;
}
/**
 * Every distinct calling code, for a split that does not depend on a number being complete.
 *
 * There are 206 of them, one to three digits, and **not one is a prefix of another** — so at most
 * one can match the head of a number and a longest-first scan is exact rather than a best guess.
 * That property is what lets {@link splitPhone} work on a half-typed value, and it is asserted in
 * `__tests__/phone.test.ts` because the split silently stops being correct the day it is false.
 */
const CALLING_CODES = new Set(getCountries().map((region) => getCountryCallingCode(region)));
/** The longest calling code there is, which bounds the scan below. */
const LONGEST_CALLING_CODE = 3;
/** The calling code at the head of a run of digits, or `null` when none of them is one. */
function leadingCallingCode(digits) {
    for (let length = LONGEST_CALLING_CODE; length >= 1; length -= 1) {
        const candidate = digits.slice(0, length);
        if (candidate.length === length && CALLING_CODES.has(candidate)) {
            return candidate;
        }
    }
    return null;
}
/** Just the digits, which is what both halves of a split number are made of. */
function digitsOf(value) {
    return value.replace(/\D/gu, '');
}
/** The calling code of a region, or the empty string for a region with no numbering plan. */
function callingCodeOf(region) {
    const known = asRegion(region);
    return known === undefined ? '' : getCountryCallingCode(known);
}
/**
 * A stored number as the two halves a form shows, for any string at all.
 *
 * **Total, and lexical before it is semantic, because the control calls it on every keystroke.**
 * A half-typed number is not `isValid()`, so a split that asked the parser first would have nothing
 * to say about `+3461` and the prefix would flicker or vanish while somebody was typing. The
 * leading calling code is therefore read off the digits — unambiguous, see {@link CALLING_CODES} —
 * and the parser is consulted only for the one thing the digits cannot say, which is WHICH region a
 * shared code belongs to.
 *
 * **It returns `nationalNumber`, never `formatNational()`.** Morocco and Ukraine put a trunk `0` in
 * the national format, and a trunk prefix is not part of the E.164 significant number: a formatted
 * national half would not survive being handed back to {@link joinPhone}.
 *
 * A value that will not parse at all is returned as its digits under the fallback region rather than
 * discarded, so a number an API client wrote in some other shape is still shown to the operator who
 * has to correct it.
 */
export function splitPhone(value, fallbackRegion = DEFAULT_PHONE_COUNTRY) {
    const known = asRegion(fallbackRegion);
    const fallback = {
        region: known === undefined ? null : fallbackRegion,
        callingCode: callingCodeOf(fallbackRegion),
        national: '',
    };
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return fallback;
    }
    if (trimmed.startsWith('+')) {
        const digits = digitsOf(trimmed);
        const code = leadingCallingCode(digits);
        if (code === null) {
            return { ...fallback, national: digits };
        }
        const parsed = parsePhoneNumberFromString(trimmed);
        return {
            region: parsed?.isValid() === true ? (parsed.country ?? null) : null,
            callingCode: code,
            national: digits.slice(code.length),
        };
    }
    const parsed = parsePhoneNumberFromString(trimmed, known === undefined ? {} : { defaultCountry: known });
    if (parsed?.isValid() === true) {
        return {
            region: parsed.country ?? null,
            callingCode: parsed.countryCallingCode,
            national: parsed.nationalNumber,
        };
    }
    return { ...fallback, national: digitsOf(trimmed) };
}
/**
 * The two halves as the one string this product stores.
 *
 * **The join is region-aware, and that is the whole reason it is a function rather than a `+`.** An
 * operator reading a Moroccan number off a document types the national form `0612345678`; pasting
 * the calling code in front of it gives `+2120612345678`, which is not that person's number and is
 * refused at save. The numbering plan knows what a trunk prefix is, so the plan is asked first and
 * string concatenation is only the fallback for a number it cannot yet make sense of — a half-typed
 * one, which is refused at save anyway and for the right reason.
 *
 * **A blank national part is the empty string, and the decision lives here.** The form omits a blank
 * optional field rather than sending `''`, so a prefix chosen with no number after it has to compose
 * to nothing; deciding that in the control instead would put the rule one layer away from the thing
 * that has to hold it.
 */
export function joinPhone(parts) {
    const typed = parts.national.trim();
    if (typed.length === 0) {
        return '';
    }
    // An international number handed in as the national half carries its own prefix, so it is that
    // number rather than a national one. Honoured here so the result can never read as two calling
    // codes concatenated.
    if (typed.startsWith('+') || typed.startsWith('00')) {
        const international = splitPhone(typed.replace(/^00/u, '+'));
        return `+${international.callingCode}${international.national}`;
    }
    const region = parts.region === null ? undefined : asRegion(parts.region);
    if (region !== undefined) {
        const parsed = parsePhoneNumberFromString(typed, { defaultCountry: region });
        if (parsed?.isValid() === true && parsed.countryCallingCode === parts.callingCode) {
            return parsed.number;
        }
    }
    return `+${parts.callingCode}${digitsOf(typed)}`;
}
/**
 * A stored number as something a person reads, or the value itself.
 *
 * Grouping belongs where a number is READ and not where it is typed: `+34 612 34 56 78` is easier to
 * check against a document than `+34612345678`, while a form that regrouped as somebody typed would
 * be moving the caret under them. A value that does not parse is returned untouched rather than
 * blanked, because a record still has to show what it holds.
 */
export function formatPhone(value) {
    const parsed = parsePhoneNumberFromString(value.trim());
    return parsed?.isValid() === true ? parsed.formatInternational() : value;
}
//# sourceMappingURL=phone.js.map